import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getTemplateEventLog,
  getTemplatePartyData,
  getTemplates,
  removeTemplate,
  updateTemplatePartiesRequest,
  updateTemplatePreferencesRequest,
  updateTemplateRecipientsRequest,
} from "../../../services/api/templates/templatesApi";

export const fetchTemplates = createAsyncThunk(
  "templates/fetchTemplates",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getTemplates(params);
      const { templates, counts } = response.data.data;

      return {
        templates: templates.data,
        counts,
        pagination: {
          current_page: templates.current_page,
          last_page: templates.last_page,
          total: templates.total,
          per_page: templates.per_page,
          next_page_url: templates.next_page_url,
          last_page_url: templates.last_page_url,
        },
      };
    } catch (error) {
      return rejectWithValue("Failed to fetch templates");
    }
  },
);

export const deleteTemplate = createAsyncThunk(
  "templates/deleteTemplate",
  async (templateId, { rejectWithValue }) => {
    try {
      await removeTemplate(templateId);
      return templateId;
    } catch (error) {
      return rejectWithValue("Failed to delete template");
    }
  },
);

export const fetchTemplatePartyData = createAsyncThunk(
  "templates/fetchTemplatePartyData",
  async (template_id, { rejectWithValue }) => {
    try {
      const response = await getTemplatePartyData(template_id);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch template party data");
    }
  },
);

export const updateTemplateRecipients = createAsyncThunk(
  "templates/updateTemplateRecipients",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateTemplateRecipientsRequest(payload);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update template recipients");
    }
  },
);

export const updateTemplatePreferences = createAsyncThunk(
  "templates/updateTemplatePreferences",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateTemplatePreferencesRequest(payload);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update template preferences");
    }
  },
);

export const updateTemplateParties = createAsyncThunk(
  "templates/updateTemplateParties",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateTemplatePartiesRequest(payload);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update template parties");
    }
  },
);

export const fetchTemplateEventLog = createAsyncThunk(
  "templates/fetchTemplateEventLog",
  async (template_id, { rejectWithValue }) => {
    try {
      const response = await getTemplateEventLog(template_id);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch template event log");
    }
  },
);

const templatesSlice = createSlice({
  name: "templates",
  initialState: {
    templateData: [],
    templateCounts: null,
    eventLog: [],
    eventLogLoading: false,
    eventLogError: null,
    currentPage: 1,
    lastPage: 1,
    totalTemplates: 0,
    perPage: 10,
    nextPageUrl: null,
    lastPageUrl: null,
    loading: false,
    error: null,
    templatePartyData: null,
    templatePartyDataLoading: false,
    templatePartyDataError: null,
    updateRecipientsLoading: false,
    updateRecipientsError: null,
    updateRecipientsSuccess: false,
    updatePreferencesLoading: false,
    updatePreferencesError: null,
    updatePreferencesSuccess: false,
    updatePartiesLoading: false,
    updatePartiesError: null,
    updatePartiesSuccess: false,
  },
  reducers: {
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setPerPage(state, action) {
      state.perPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templateData = action.payload.templates;
        state.templateCounts = action.payload.counts;
        state.currentPage = action.payload.pagination.current_page;
        state.lastPage = action.payload.pagination.last_page;
        state.totalTemplates = action.payload.pagination.total;
        state.perPage = action.payload.pagination.per_page || state.perPage;
        state.nextPageUrl = action.payload.pagination.next_page_url || null;
        state.lastPageUrl = action.payload.pagination.last_page_url || null;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.templateData = state.templateData.filter(
          (template) => template.id !== action.payload,
        );
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchTemplatePartyData.pending, (state) => {
        state.templatePartyDataLoading = true;
        state.templatePartyDataError = null;
      })
      .addCase(fetchTemplatePartyData.fulfilled, (state, action) => {
        state.templatePartyDataLoading = false;
        state.templatePartyData = action.payload;
      })
      .addCase(fetchTemplatePartyData.rejected, (state, action) => {
        state.templatePartyDataLoading = false;
        state.templatePartyDataError = action.payload;
      })
      .addCase(updateTemplateRecipients.pending, (state) => {
        state.updateRecipientsLoading = true;
        state.updateRecipientsError = null;
        state.updateRecipientsSuccess = false;
      })
      .addCase(updateTemplateRecipients.fulfilled, (state) => {
        state.updateRecipientsLoading = false;
        state.updateRecipientsSuccess = true;
      })
      .addCase(updateTemplateRecipients.rejected, (state, action) => {
        state.updateRecipientsLoading = false;
        state.updateRecipientsError = action.payload;
        state.updateRecipientsSuccess = false;
      })
      .addCase(updateTemplatePreferences.pending, (state) => {
        state.updatePreferencesLoading = true;
        state.updatePreferencesError = null;
        state.updatePreferencesSuccess = false;
      })
      .addCase(updateTemplatePreferences.fulfilled, (state) => {
        state.updatePreferencesLoading = false;
        state.updatePreferencesSuccess = true;
      })
      .addCase(updateTemplatePreferences.rejected, (state, action) => {
        state.updatePreferencesLoading = false;
        state.updatePreferencesError = action.payload;
        state.updatePreferencesSuccess = false;
      })
      .addCase(updateTemplateParties.pending, (state) => {
        state.updatePartiesLoading = true;
        state.updatePartiesError = null;
        state.updatePartiesSuccess = false;
      })
      .addCase(updateTemplateParties.fulfilled, (state) => {
        state.updatePartiesLoading = false;
        state.updatePartiesSuccess = true;
      })
      .addCase(updateTemplateParties.rejected, (state, action) => {
        state.updatePartiesLoading = false;
        state.updatePartiesError = action.payload;
        state.updatePartiesSuccess = false;
      })
      .addCase(fetchTemplateEventLog.pending, (state) => {
        state.eventLogLoading = true;
        state.eventLogError = null;
      })
      .addCase(fetchTemplateEventLog.fulfilled, (state, action) => {
        state.eventLogLoading = false;
        state.eventLog = action.payload;
      })
      .addCase(fetchTemplateEventLog.rejected, (state, action) => {
        state.eventLogLoading = false;
        state.eventLogError = action.payload;
      });
  },
});

export const { setCurrentPage, setPerPage } = templatesSlice.actions;

export default templatesSlice.reducer;
